import { useCallback, useMemo } from 'react'

import { faPencil, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Modal } from 'govwell-ui'
import { AIAssistantChatAssociatedAIChatbotFAQFragment } from 'types/graphql'

import {
  useCreateAIChatbotFAQMutation,
  useUpdateAIChatbotFAQMutation,
} from 'src/fetch/aiChatbotFAQ'
import { useAppForm } from 'src/hooks/use-app-form'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'

type Props = {
  onSubmit: () => Promise<unknown>
  onClose: () => void
} & (
  | {
      aiChatbotFAQ: AIAssistantChatAssociatedAIChatbotFAQFragment
      modalType: 'edit'
    }
  | {
      aiChatbotFAQ?: never
      modalType: 'create'
    }
)

const CustomAnswerModal = (props: Props) => {
  const { modalType, aiChatbotFAQ, onClose, onSubmit } = props
  const { mutateAsync: updateAIChatbotFAQ } = useUpdateAIChatbotFAQMutation()
  const { mutateAsync: createAIChatbotFAQ } = useCreateAIChatbotFAQMutation()
  const organizationId = useMyOrgId()

  const initialValues = useMemo(() => {
    return {
      question: aiChatbotFAQ?.question ?? '',
      answer: aiChatbotFAQ?.answer ?? '',
    }
  }, [aiChatbotFAQ])

  const form = useAppForm({
    defaultValues: initialValues,
  })

  const handleSubmit = useCallback(async () => {
    const answer = form.getFieldValue('answer')
    const question = form.getFieldValue('question')
    if (modalType === 'edit') {
      await updateAIChatbotFAQ({
        id: aiChatbotFAQ.id,
        input: {
          answer,
          question,
        },
      })
    } else if (modalType === 'create') {
      await createAIChatbotFAQ({
        input: {
          answer,
          question,
          organizationId,
        },
      })
    }
    await onSubmit()
  }, [
    aiChatbotFAQ?.id,
    createAIChatbotFAQ,
    form,
    modalType,
    onSubmit,
    organizationId,
    updateAIChatbotFAQ,
  ])

  return (
    <Modal onClose={onClose} width="50%">
      <Modal.Header icon={modalType === 'edit' ? faPencil : faPlus}>
        {`${modalType === 'edit' ? 'Update' : 'Create'} Custom Answer`}
      </Modal.Header>
      <Modal.Content>
        <Flex direction="column" gap={6}>
          <form.AppField name="question">
            {(field) => (
              <field.TextArea
                caption={`${modalType === 'edit' ? 'Update' : 'Create'} the question for this Custom Answer`}
                field={field}
                isRequired
                label="Question"
                minRows={3}
                maxRows={5}
                placeholder="Enter the question"
              />
            )}
          </form.AppField>
          <form.AppField name="answer">
            {(field) => (
              <field.RichTextInput
                caption={`${modalType === 'edit' ? 'Update' : 'Create'} the answer for this Custom Answer`}
                field={field}
                label="Answer"
                isRequired
              />
            )}
          </form.AppField>
        </Flex>
      </Modal.Content>
      <Modal.Footer
        confirmText={`${modalType === 'edit' ? 'Update' : 'Create'} Custom Answer`}
        onConfirm={handleSubmit}
      />
    </Modal>
  )
}

export default React.memo(CustomAnswerModal)
